import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../config";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import {
  TrendingUp, Eye, Loader2, Copy, Check, ArrowUp, ArrowDown, Minus,
  BarChart3, DollarSign, Users, Zap, Plus, Globe
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { OrderNowBottomCTA } from "../components/common/ServiceOrderBundle";
import { SignInGate } from "./CompetitorSpyPage";

const ENGINE_COLORS = { chatgpt: "#10A37F", perplexity: "#20808D", gemini: "#4285F4", copilot: "#7C3AED", google_ai: "#EA4335" };

export default function AttributionPage() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [attrs, setAttrs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [setupUrl, setSetupUrl] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!user) return;
    fetchAttrs();
  }, [user]);

  const fetchAttrs = async () => {
    try {
      const r = await axios.get(`${API}/attribution/status?email=${encodeURIComponent(user.email)}`);
      setAttrs(r.data.attributions || []);
      if (r.data.attributions?.length > 0) {
        setSelected(r.data.attributions[0]);
        fetchDashboard(r.data.attributions[0].attr_id);
      }
    } catch {}
  };

  const fetchDashboard = async (attrId) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/attribution/dashboard/${attrId}`);
      setDashboard(r.data);
    } catch {}
    setLoading(false);
  };

  const handleSetup = async () => {
    if (!setupUrl.trim()) return;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/attribution/setup`, { website_url: setupUrl.trim(), email: user.email, language: language || "en" });
      setShowSetup(false);
      setSetupUrl("");
      await fetchAttrs();
      setSelected(r.data);
      fetchDashboard(r.data.attr_id);
    } catch {}
    setLoading(false);
  };

  const TrendIcon = ({ trend }) => {
    if (trend === "up") return <ArrowUp className="w-3 h-3 text-emerald-400" />;
    if (trend === "down") return <ArrowDown className="w-3 h-3 text-red-400" />;
    return <Minus className="w-3 h-3 text-[var(--color-text-muted)]" />;
  };

  const finalCta = (
    <OrderNowBottomCTA
      packageId="addon_attribution"
      priceEur={39}
      interval="mes"
      serviceName="AI Attribution"
      headline="Zisti, ktorá AI ti prináša zákazníkov"
      subcopy="Prestaň hádať. Reálne čísla z ChatGPT, Perplexity, Gemini a Copilot — s per-engine ROI v €."
      testid="attribution-final-cta"
    />
  );

  if (!user) return (
    <SignInGate icon={BarChart3} title={t.attr_title || "AI Traffic Attribution"} sub={t.attr_sign_in_desc || "Sign in to see how much traffic AI brings you."}>
      {finalCta}
    </SignInGate>
  );

  const s = dashboard?.summary;

  return (
    <div className="min-h-screen bg-[var(--color-bg)] text-white">
      <header className="bg-[var(--color-bg)]/60 backdrop-blur-xl border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#a855f7] flex items-center justify-center"><TrendingUp className="w-5 h-5 text-white" /></div>
            <span className="text-xl font-black font-['Cabinet_Grotesk']">TopRankPulse</span>
          </a>
          <div className="flex items-center gap-4">
            <a href="/dashboard" className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] font-['JetBrains_Mono'] uppercase">{t.nav_dashboard || "Dashboard"}</a>
            <a href="/brand-monitor" className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] font-['JetBrains_Mono'] uppercase">{t.nav_monitor || "AI Monitor"}</a>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <BarChart3 className="w-6 h-6 text-[#a855f7]" />
              <h1 className="text-3xl font-black font-['Cabinet_Grotesk'] tracking-tight" data-testid="attribution-title">{t.attr_title || "AI Traffic Attribution"}</h1>
              <span className="px-2 py-0.5 bg-[#FFE600]/10 text-[#FFE600] text-xs font-bold">€29/mo</span>
            </div>
            <p className="text-[var(--color-text-muted)]">{t.attr_desc || "See exactly how much traffic and revenue AI search engines bring you"}</p>
          </div>
          <button onClick={() => setShowSetup(true)} className="flex items-center gap-2 px-6 py-3 bg-[#a855f7] text-white font-bold uppercase text-sm" data-testid="add-attr-btn">
            <Plus className="w-4 h-4" /> {t.attr_add || "Add Site"}
          </button>
        </div>

        {/* Setup modal */}
        {showSetup && (
          <div className="fixed inset-0 bg-[var(--color-bg)]/80 z-50 flex items-center justify-center p-4">
            <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-8 max-w-md w-full">
              <h2 className="text-xl font-black font-['Cabinet_Grotesk'] mb-4">{t.attr_setup || "Setup Attribution"}</h2>
              <input value={setupUrl} onChange={e => setSetupUrl(e.target.value)} placeholder="https://yoursite.com"
                className="w-full px-4 py-3 bg-[var(--color-bg)] border border-[var(--color-border)] text-white mb-4 focus:border-[#a855f7] focus:outline-none" data-testid="attr-url" />
              <button onClick={handleSetup} disabled={loading} className="w-full py-3 bg-[#a855f7] text-white font-bold uppercase" data-testid="attr-submit">
                {loading ? (t.attr_setting_up || "Setting up...") : (t.attr_start || "Start Tracking")}
              </button>
              <button onClick={() => setShowSetup(false)} className="w-full mt-2 text-[var(--color-text-muted)] text-sm">{t.attr_cancel || "Cancel"}</button>
            </div>
          </div>
        )}

        {/* Dashboard */}
        {dashboard && s && (
          <div className="space-y-6" data-testid="attribution-dashboard">
            {dashboard.is_demo && (
              <div className="bg-[#FFE600]/5 border border-[#FFE600]/20 p-4 text-center text-sm text-[#FFE600]">
                {t.attr_demo || "Demo data — install the tracking pixel to see real numbers"}
              </div>
            )}

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-5">
                <p className="text-[var(--color-text-muted)] text-xs font-['JetBrains_Mono'] uppercase">{t.attr_visits || "AI Visits"}</p>
                <p className="text-3xl font-black font-['JetBrains_Mono'] text-white mt-1">{s.total_ai_visits?.toLocaleString()}</p>
              </div>
              <div className="bg-[var(--color-surface)] border border-emerald-800/30 p-5">
                <p className="text-[var(--color-text-muted)] text-xs font-['JetBrains_Mono'] uppercase">{t.attr_conversions || "Conversions"}</p>
                <p className="text-3xl font-black font-['JetBrains_Mono'] text-emerald-400 mt-1">{s.total_conversions}</p>
              </div>
              <div className="bg-[var(--color-surface)] border border-[#FFE600]/30 p-5">
                <p className="text-[var(--color-text-muted)] text-xs font-['JetBrains_Mono'] uppercase">{t.attr_revenue || "Revenue"}</p>
                <p className="text-3xl font-black font-['JetBrains_Mono'] text-[#FFE600] mt-1">€{s.total_revenue?.toLocaleString()}</p>
              </div>
              <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-5">
                <p className="text-[var(--color-text-muted)] text-xs font-['JetBrains_Mono'] uppercase">{t.attr_conv_rate || "Conv. Rate"}</p>
                <p className="text-3xl font-black font-['JetBrains_Mono'] text-white mt-1">{s.conversion_rate}%</p>
              </div>
              <div className="bg-[var(--color-surface)] border border-[#a855f7]/30 p-5">
                <p className="text-[var(--color-text-muted)] text-xs font-['JetBrains_Mono'] uppercase">ROI</p>
                <p className="text-3xl font-black font-['JetBrains_Mono'] text-[#a855f7] mt-1">{s.roi}x</p>
              </div>
            </div>

            {/* Daily Trend Chart */}
            <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-6">
              <h3 className="text-sm font-bold text-[var(--color-text-muted)] uppercase tracking-wider mb-4 font-['JetBrains_Mono']">{t.attr_traffic_30 || "AI Traffic (30 Days)"}</h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={dashboard.daily_trend || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                  <XAxis dataKey="date" tick={{fontSize: 10, fill: '#666'}} />
                  <YAxis tick={{fontSize: 11, fill: '#666'}} />
                  <Tooltip contentStyle={{background:'#141414', border:'1px solid #333', color:'#fff'}} />
                  <Bar dataKey="visits" fill="#a855f7" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Sources */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-6">
                <h3 className="text-sm font-bold text-[var(--color-text-muted)] uppercase tracking-wider mb-4 font-['JetBrains_Mono']">{t.attr_by_source || "Traffic by AI Source"}</h3>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={(dashboard.sources || []).map(s => ({name: s.name, value: s.visits}))} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80}
                      label={({name, value}) => `${name}: ${value}`}>
                      {(dashboard.sources || []).map((s, i) => <Cell key={i} fill={ENGINE_COLORS[s.id] || '#666'} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-6">
                <h3 className="text-sm font-bold text-[var(--color-text-muted)] uppercase tracking-wider mb-4 font-['JetBrains_Mono']">{t.attr_source_details || "Source Details"}</h3>
                <div className="space-y-3">
                  {(dashboard.sources || []).map((src, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-[var(--color-border)] last:border-0">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full" style={{backgroundColor: ENGINE_COLORS[src.id] || '#666'}} />
                        <span className="text-sm font-bold text-white">{src.name}</span>
                        <TrendIcon trend={src.trend} />
                      </div>
                      <div className="text-right">
                        <span className="text-sm font-mono text-white">{src.visits}</span>
                        <span className="text-xs text-[var(--color-text-muted)] ml-2">{t.attr_visits_short || "visits"}</span>
                        <span className="text-xs text-emerald-400 ml-3">€{src.revenue}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Insights */}
            {dashboard.insights?.length > 0 && (
              <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-6" data-testid="attr-insights">
                <h3 className="text-sm font-bold text-[var(--color-text-muted)] uppercase tracking-wider mb-4 font-['JetBrains_Mono']">{t.attr_insights || "AI Insights"}</h3>
                <div className="space-y-2">
                  {dashboard.insights.map((insight, i) => (
                    <div key={i} className="flex items-start gap-3 py-2">
                      <Zap className="w-4 h-4 text-[#FFE600] flex-shrink-0 mt-0.5" />
                      <p className="text-[var(--color-text)] text-sm">{insight}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pixel Code */}
            {selected?.pixel_code && (
              <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-[var(--color-text-muted)] uppercase tracking-wider font-['JetBrains_Mono']">{t.attr_pixel || "Tracking Pixel"}</h3>
                  <button onClick={() => { navigator.clipboard.writeText(selected.pixel_code); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                    className="flex items-center gap-1 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)]" data-testid="copy-pixel">
                    {copied ? <><Check className="w-3 h-3 text-emerald-400" />{t.attr_copied || "Copied"}</> : <><Copy className="w-3 h-3" />{t.attr_copy || "Copy"}</>}
                  </button>
                </div>
                <pre className="bg-black p-4 overflow-x-auto text-sm font-['JetBrains_Mono'] text-emerald-400">
                  <code>{selected.pixel_code}</code>
                </pre>
                <p className="text-[var(--color-text-muted)] text-xs mt-2">{t.attr_pixel_desc || "Paste this before </head> on your website to start tracking real AI traffic."}</p>
              </div>
            )}
          </div>
        )}

        {/* Empty state */}
        {!dashboard && !loading && attrs.length === 0 && (
          <div className="text-center py-20">
            <BarChart3 className="w-16 h-16 text-zinc-700 mx-auto mb-6" />
            <h2 className="text-2xl font-black font-['Cabinet_Grotesk'] mb-3">{t.attr_no_sites || "No Sites Tracked Yet"}</h2>
            <p className="text-[var(--color-text-muted)] mb-8">{t.attr_no_sites_desc || "Add your website to see how much traffic AI search engines bring you."}</p>
            <button onClick={() => setShowSetup(true)} className="px-8 py-4 bg-[#a855f7] text-white font-bold uppercase text-lg" data-testid="first-attr-btn">
              <Plus className="w-5 h-5 inline mr-2" /> {t.attr_add_website || "Add Your Website"}
            </button>
          </div>
        )}

        {loading && !dashboard && <div className="text-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#a855f7] mx-auto" /></div>}
      </div>

      <footer className="py-8 px-6 bg-[var(--color-bg)] border-t border-[var(--color-border)] mt-12">
        <div className="max-w-7xl mx-auto">
          {finalCta}
        </div>
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-[#a855f7] flex items-center justify-center"><TrendingUp className="w-4 h-4 text-white" /></div>
            <span className="font-black font-['Cabinet_Grotesk']">TopRankPulse</span>
          </div>
          <p className="text-zinc-700 text-xs font-['JetBrains_Mono']">© 2026 TopRankPulse</p>
        </div>
      </footer>
    </div>
  );
}
